import { type BlobLike, FileCache } from "@pdfking/compatibility"
import {
  type AsyncSequence,
  type Bytes,
  FilterAsync,
  First,
  FullMethodDispatcher,
  Items,
  Peek,
  SafePromise,
  type Time,
  UnixTime,
  Void,
} from "@pdfking/tfunc"

import type { PdfEngine } from "../types/engine/PdfEngine"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"

import { AutoThrow } from "../errors/AutoThrow"
import { EngineError } from "../errors/EngineError"
import { FileContentGUID } from "../methods/FileContentGUID"
import { FileContentSize } from "../methods/FileContentSize"
import { ErrorName } from "../types/errors/ErrorName"
import { VirtualFileType } from "../types/files/VirtualFileType"

export type FileBound = {
  readonly modified: Time
  readonly size: Bytes
  readonly stream: AsyncSequence<BlobLike>
}

const dispatch = FullMethodDispatcher<
  [VirtualFile, PdfEngine, TrackingCallback],
  Promise<FileBound>
>({
  [VirtualFileType.GENERATED_FILE]: async (file, engine, _emit) => {
    const blob = await FileCache(engine.config.file_cache).get(
      FileContentGUID(file)
    )

    if (!blob) {
      console.error("Cache miss for file", file)
      throw EngineError(ErrorName.CACHE_EXPIRED)
    }

    return {
      modified: file.modified,
      size: blob.size as Bytes,
      stream: [blob],
    }
  },
  [VirtualFileType.MULTI_PAGE_PDF]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      engine.MergePdf(file.pages, {
        cache_evict: true,
        task_id: FileContentGUID(file),
      })
    ),

  [VirtualFileType.REMOTE_FILE]: async (file, engine, emit) => {
    const { error, stream, value } = await Peek(engine.DownloadFile(file))

    await AutoThrow(error)

    const generator = async function* () {
      for await (const chunk of stream) {
        const { stream } = await RequestBlob(engine, chunk, emit)
        yield* stream
      }
    }

    return {
      modified: UnixTime(),
      size: value!.bytes_total,
      stream: generator(),
    }
  },

  [VirtualFileType.REMOTE_FILE_CHUNK]: (
    file,
    engine,
    emit
  ): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      First(
        FilterAsync(
          f => f.bytes_start === file.bytes_start,
          engine.DownloadFile(file.remote, { cache_evict: true })
        )
      )
    ),

  [VirtualFileType.SINGLE_PAGE_IMAGE]: (file, engine, emit) =>
    dispatchWithFallback(file.content, engine, emit, () =>
      First(engine.RasterizePdf(file.source, { cache_evict: true }))
    ),

  [VirtualFileType.SINGLE_PAGE_PDF]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () => {
      if (file.source) {
        return First(
          FilterAsync(
            f => f.page_number === file.page_number,
            engine.SplitPdf(file.source, { cache_evict: true })
          )
        )
      }
      return engine.PlotPdf(file.page, file.context, {
        cache_evict: true,
        task_id: FileContentGUID(file),
      })
    }),

  [VirtualFileType.ZIP_FILE]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      engine.ZipFiles(Items(file.files), {
        cache_evict: true,
        task_id: FileContentGUID(file),
      })
    ),
})

const dispatchWithFallback = async (
  file: GeneratedFile | RemoteFile,
  engine: PdfEngine,
  emit: TrackingCallback = Void,
  fallback: () => Promise<VirtualFile>
): Promise<FileBound> => {
  const { error, value } = await SafePromise(dispatch(file, engine, emit))

  if (error) {
    const computed = await fallback()

    if (FileContentGUID(computed) === FileContentGUID(file)) {
      return dispatch(computed, engine, emit)
    }

    if (FileContentSize(computed) === FileContentSize(file)) {
      console.error("⚠️ sha256 failed, size passed", { computed, file })

      return dispatch(computed, engine, emit)
    }

    throw EngineError(ErrorName.CACHE_MISMATCH)
  }

  return value
}

export const RequestBlob = (
  engine: PdfEngine,
  file: VirtualFile,
  emit: TrackingCallback = Void
): Promise<FileBound> => dispatch(file, engine, emit)
